import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  ButtonV2,
  LabraToolTipV2,
  TextArea,
  TextInputV2,
} from '@labrav/react-components'
import React from 'react'
import { faList } from '@fortawesome/pro-regular-svg-icons'
import { Typography } from '@mui/material'
import { FormikProps, useFormikContext } from 'formik'
import { useStyles } from './FlyOutProductLandingPageConfiguration.styles'
import { FormLockedType } from '../../../types/FormLockedType'
import { FlyoutProductSections } from '../../../types/addEditProduct'
import { FlyoutProductLandingPageConfigurationFaqs } from './FlyoutProductLandingePageConfigurationFaqs'

type FlyOutProductLandingPageConfigurationProps = {
  detailView?: boolean
  detailViewTooltip?: string
  handleRefLinkClick?: (inex: number) => void
}

export const FlyOutProductLandingPageConfiguration: React.FC<
  FormLockedType & FlyOutProductLandingPageConfigurationProps
> = ({ isFormLocked, detailViewTooltip, handleRefLinkClick, detailView }) => {
  const { classes } = useStyles()
  const { values, errors, touched, handleBlur, handleChange } =
    useFormikContext() as FormikProps<FlyoutProductSections>
  const { infoCollectionFormMetaData } = values

  const [indexModal, setIndexModal] = React.useState(0)

  return (
    <BoxV2
      heading={'Buyer registration page configuration'}
      headingIcon={
        <FontAwesomeIcon
          icon={faList}
          style={{ width: '20px', height: '16px' }}
        />
      }
      secondaryActions={
        <FlyoutProductLandingPageConfigurationFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
    >
      <>
        <div
          className={classes.sectionContainer}
          data-testid={'landing-page-configuration'}
        >
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Heading message
            </Typography>
            <Typography className={classes.sectionText}>
              This text appears at the top of the white box and above all input
              fields. It serves as the heading for your buyer registration page.{' '}
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
              >
                (Refer no. 3 in image no. 1)
              </ButtonV2>
            </Typography>
          </div>
          <div className={classes.textWidthContainer}>
            <LabraToolTipV2
              followCursor={detailView}
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
              className={classes.textWidthFull}
            >
              <div className={classes.textWidthFull}>
                <TextInputV2
                  keyName="infoCollectionFormMetaData.headingMessage"
                  title="Heading message"
                  type="text"
                  placeHolder=""
                  value={infoCollectionFormMetaData?.headingMessage}
                  isError={
                    !!(
                      errors?.infoCollectionFormMetaData?.headingMessage &&
                      touched?.infoCollectionFormMetaData?.headingMessage
                    )
                  }
                  maxCharacters={2000}
                  onChange={handleChange}
                  isReadOnly={isFormLocked as boolean}
                  isRequired={false}
                  errorText={
                    (!!touched?.infoCollectionFormMetaData?.headingMessage &&
                      errors?.infoCollectionFormMetaData?.headingMessage) ||
                    ''
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                  updatedValue={infoCollectionFormMetaData?.headingMessage}
                  viewOnly={detailView}
                />
              </div>
            </LabraToolTipV2>
          </div>
        </div>

        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Form submission success message
            </Typography>
            <Typography className={classes.sectionText}>
              This message is displayed to the buyer after they submit the form
              on the buyer registration page. They will see this message before
              being automatically redirected to your Application's login page
              URL in 5 seconds, where they can log in.{' '}
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(1)}
              >
                (Refer no. 7 in image no. 2)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            followCursor={detailView}
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            className={classes.textWidthFull}
          >
            <div className={classes.textWidthFull}>
              <TextInputV2
                keyName="infoCollectionFormMetaData.submissionSuccessMessage"
                title="Form submission success message"
                type="text"
                placeHolder=""
                value={infoCollectionFormMetaData?.submissionSuccessMessage}
                isError={
                  !!(
                    errors?.infoCollectionFormMetaData
                      ?.submissionSuccessMessage &&
                    touched?.infoCollectionFormMetaData
                      ?.submissionSuccessMessage
                  )
                }
                maxCharacters={2000}
                onChange={handleChange}
                isReadOnly={isFormLocked as boolean}
                viewOnly={detailView}
                isRequired={false}
                errorText={
                  (!!touched?.infoCollectionFormMetaData
                    ?.submissionSuccessMessage &&
                    errors?.infoCollectionFormMetaData
                      ?.submissionSuccessMessage) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
                updatedValue={
                  infoCollectionFormMetaData?.submissionSuccessMessage
                }
              />
            </div>
          </LabraToolTipV2>
        </div>

        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <div className={classes.sectionHeadingContainer}>
              {' '}
              <Typography className={classes.sectionHeading}>
                Existing buyer message
              </Typography>
            </div>
            <Typography className={classes.sectionText}>
              This message is displayed to buyers who have previously submitted
              the buyer registration page form and revisit the page. They will
              see this message and be automatically redirected to your
              Application's login page after 5 seconds, where they can log in.{' '}
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(2)}
              >
                (Refer no. 8 in image no. 3)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            followCursor={detailView}
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            className={classes.textWidthFull}
          >
            <div className={classes.textWidthFull}>
              <TextInputV2
                keyName="infoCollectionFormMetaData.existingBuyerMessage"
                title="Existing buyer message"
                type="text"
                placeHolder=""
                value={infoCollectionFormMetaData?.existingBuyerMessage}
                maxCharacters={2000}
                isError={
                  !!(
                    errors?.infoCollectionFormMetaData?.existingBuyerMessage &&
                    touched?.infoCollectionFormMetaData?.existingBuyerMessage
                  )
                }
                onChange={handleChange}
                isReadOnly={isFormLocked as boolean}
                viewOnly={detailView}
                isRequired={false}
                errorText={
                  (!!touched?.infoCollectionFormMetaData
                    ?.existingBuyerMessage &&
                    errors?.infoCollectionFormMetaData?.existingBuyerMessage) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
                updatedValue={infoCollectionFormMetaData?.existingBuyerMessage}
              />
            </div>
          </LabraToolTipV2>
        </div>

        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <div className={classes.sectionHeadingContainer}>
              <Typography className={classes.sectionHeading}>
                Email custom message
              </Typography>
              <Typography className={classes.sectionText}>Optional</Typography>
            </div>
            <Typography className={classes.sectionText}>
              This message appears as the last paragraph in the email that Labra
              sends to your buyer confirming their purchase. The email is sent
              immediately after they submit the form on the buyer registration
              page.{' '}
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(3)}
              >
                (Refer no. 12 in image no. 4)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            followCursor={detailView}
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            className={classes.textWidthFull}
          >
            <div className={classes.textWidthFull}>
              <TextArea
                keyName="infoCollectionFormMetaData.emailCustomMessage"
                title="Enter an email custom message"
                type="text"
                placeHolder=""
                value={infoCollectionFormMetaData?.emailCustomMessage}
                maxCharacters={2000}
                isError={
                  !!(
                    errors?.infoCollectionFormMetaData?.emailCustomMessage &&
                    touched?.infoCollectionFormMetaData?.emailCustomMessage
                  )
                }
                onChange={handleChange}
                isReadOnly={isFormLocked as boolean}
                viewOnly={detailView}
                isRequired={false}
                errorText={
                  (!!touched?.infoCollectionFormMetaData?.emailCustomMessage &&
                    errors?.infoCollectionFormMetaData?.emailCustomMessage) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
                rows={3}
                height={80}
                updatedValue={infoCollectionFormMetaData?.emailCustomMessage}
              />
            </div>
          </LabraToolTipV2>
        </div>
      </>
    </BoxV2>
  )
}
